import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Play, ShoppingBag, Sparkles, CheckCircle2 } from "lucide-react";

const TYPE_META = {
  live: { label: "Live now", Icon: Radio, tint: "#FF3355" },
  video: { label: "Watch", Icon: Play, tint: "#6EE7FF" },
  product: { label: "In the shop", Icon: ShoppingBag, tint: "#FFD54A" },
};

// A friendly, non-intrusive discovery card woven into the Pluse feed. It is
// clearly labelled "Suggested for you" (never disguised as a post) and can be
// switched off entirely by admins.
export default function SuggestionCard({ item, delay = 0 }) {
  if (!item) return null;
  const meta = TYPE_META[item.type] || TYPE_META.video;
  const { Icon } = meta;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      data-testid={`suggestion-card-${item.type}-${item.id}`}
      className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03] relative"
    >
      <div className="flex items-center justify-between px-4 pt-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white/45">
          <Sparkles className="w-3 h-3 text-[#9387FE]" /> Suggested for you
        </div>
        {item.pinned && (
          <span className="text-[9px] uppercase tracking-widest text-[#FFD54A]/80">Featured</span>
        )}
      </div>

      <Link to={item.link || "#"} className="block group" data-testid={`suggestion-link-${item.id}`}>
        <div className="flex gap-4 p-4">
          <div className="relative w-36 h-24 rounded-2xl overflow-hidden shrink-0 bg-white/5">
            {item.thumbnail ? (
              <img src={item.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/20">
                <Icon className="w-7 h-7" />
              </div>
            )}
            <span
              className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-black"
              style={{ background: meta.tint }}
            >
              <Icon className="w-3 h-3" /> {meta.label}
            </span>
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <div className="font-semibold text-sm leading-snug line-clamp-2 group-hover:gradient-text transition-colors">
              {item.title || "Untitled"}
            </div>
            {item.creator && (
              <div className="flex items-center gap-2 mt-2">
                {item.creator.avatar_url && (
                  <img src={item.creator.avatar_url} alt="" className="w-6 h-6 rounded-full border border-white/10" />
                )}
                <span className="text-xs text-white/60 flex items-center gap-1 truncate">
                  {item.creator.name || item.creator.username}
                  {item.creator.verified && <CheckCircle2 className="w-3 h-3 text-[#6EE7FF]" />}
                </span>
              </div>
            )}
            {item.type === "product" && item.price != null && (
              <div className="text-sm font-bold text-[#FFD54A] mt-2">₹{Number(item.price).toFixed(0)}</div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
